import * as Sentry from '@sentry/react';
import { z } from 'zod';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { createSelectors } from '~/store/utils/createSelectors';

export const schemaChatDraftEntry = z
    .object({
        content: z.string(),
        headerNamedValues: z.record(z.string(), z.string()).optional(),
        headerPositionalValues: z.array(z.string()).optional(),
        namedValues: z.record(z.string(), z.string()).optional(),
        positionalValues: z.array(z.string()).optional(),
        templateLanguage: z.string().optional(),
        templateName: z.string().optional(),
    })
    .superRefine((data, ctx) => {
        // Rule 1: templateName and templateLanguage must both be present or both absent
        const hasTemplateName = data.templateName !== undefined;
        const hasTemplateLanguage = data.templateLanguage !== undefined;
        if (hasTemplateName !== hasTemplateLanguage) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'templateName and templateLanguage must both be present or both absent',
                path: hasTemplateName ? ['templateLanguage'] : ['templateName'],
            });
        }

        const hasPositionalValues = data.positionalValues !== undefined || data.headerPositionalValues !== undefined;
        const positionalPath: Array<string> =
            data.positionalValues !== undefined ? ['positionalValues'] : ['headerPositionalValues'];

        // headerNamedValues belongs to the named family — a NAMED header's own slot overrides.
        const hasNamedValues = data.namedValues !== undefined || data.headerNamedValues !== undefined;
        const namedPath: Array<string> = data.namedValues !== undefined ? ['namedValues'] : ['headerNamedValues'];

        // Rule 2: If templateName is absent, named values and positional values must all be absent
        if (!hasTemplateName && (hasNamedValues || hasPositionalValues)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    'namedValues, headerNamedValues, positionalValues and headerPositionalValues must be absent when templateName is absent',
                path: hasNamedValues ? namedPath : positionalPath,
            });
        }

        // Rule 3: If templateName is present, named values and positional values must not both be present
        if (hasTemplateName && hasNamedValues && hasPositionalValues) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'named values and positional values must not both be present together',
                path: namedPath,
            });
        }
    });

export type ChatDraftEntry = z.infer<typeof schemaChatDraftEntry>;

interface ChatDraftState {
    drafts: Record<string, ChatDraftEntry>;
}

const sanitizeDrafts = (persistedState: unknown): Record<string, ChatDraftEntry> => {
    if (typeof persistedState !== 'object' || persistedState === null) {
        return {};
    }

    const { drafts } = persistedState as { drafts?: unknown };
    if (typeof drafts !== 'object' || drafts === null) {
        return {};
    }

    return Object.entries(drafts).reduce<Record<string, ChatDraftEntry>>((validDrafts, [chatId, draft]) => {
        const result = schemaChatDraftEntry.safeParse(draft);
        if (!result.success) {
            Sentry.captureException(result.error, {
                extra: { chatId, issues: result.error.issues.map((issue) => issue.path.join('.')) },
            });
            return validDrafts;
        }
        return { ...validDrafts, [chatId]: result.data };
    }, {});
};

const useChatDraftBaseStore = create<ChatDraftState>()(
    persist(() => ({ drafts: {} }), {
        merge: (persistedState, currentState) => ({
            ...currentState,
            drafts: sanitizeDrafts(persistedState),
        }),
        name: 'whatsapp-chat-drafts',
        // Survive reload; die with the tab.
        storage: createJSONStorage(() => sessionStorage),
    }),
);

export const useChatDraftStore = createSelectors(useChatDraftBaseStore);

export const setChatDraft = (chatId: string, draft: ChatDraftEntry) => {
    useChatDraftBaseStore.setState((state) => ({
        drafts: { ...state.drafts, [chatId]: draft },
    }));
};

export const clearChatDraft = (chatId: string) => {
    useChatDraftBaseStore.setState((state) => {
        const drafts = { ...state.drafts };
        delete drafts[chatId];
        return { drafts };
    });
};

export const getChatDraft = (chatId: string): ChatDraftEntry | undefined =>
    useChatDraftBaseStore.getState().drafts[chatId];
